// @file Composable that enables an element to be draggable.
import {
  makeItemData,
  makeItemDataForExternal,
  type DragData,
  type DragDataForExternal,
  type ItemData,
} from '@@/pragmatic-drag-and-drop/helpers'
import { renderNativeDragPreview, type ComponentProps } from '@@/pragmatic-drag-and-drop/renderers'
import { draggable, type ElementEventPayloadMap } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/element/center-under-pointer'
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types'
import { computed, isRef, onBeforeUnmount, onMounted, ref, unref, type Component, type Ref } from 'vue'

/**
 * Makes an element draggable.
 */
export const useDraggableElement = <TData extends DragData, TDragPreviewComponentProps extends ComponentProps>({
  elementRef,
  type,
  data,
  dataForExternal,
  isDraggable,
  dragHandleElementRef,
  dragPreview,
  dragPreviewComponentProps,
  shouldDisableNativeDragPreview = false,
  skipSafariPatch = false,
  onDragStart,
  onDrag,
  onDrop,
}: {
  /**
   * Element to be made draggable.
   */
  elementRef: Ref<HTMLElement | undefined>
  /**
   * Used to differentiate multiple types of drag sources on a page.
   */
  type: ItemData['type']
  /**
   * Data to attach with this draggable element.
   */
  data: Ref<TData>
  /**
   * Data to attach with this draggable element when dropped on an external drop target.
   * Be careful not to expose sensitive information here as it can be read by third parties.
   */
  dataForExternal?: Ref<DragDataForExternal>
  /**
   * Reactive value to enable/disable dragging of this element.
   * @default true
   */
  isDraggable?: Ref<boolean>
  /**
   * Element to be used as a drag handle.
   * @default elementRef
   */
  dragHandleElementRef?: Ref<HTMLElement | undefined>
  /**
   * Vue component or HTML element to use as the drag preview.
   * @default elementRef
   */
  dragPreview?: Component | Ref<HTMLElement | undefined>
  /**
   * Props to be passed to the drag preview component.
   */
  dragPreviewComponentProps?: Ref<TDragPreviewComponentProps>
  /**
   * Whether to disable native drag preview.
   * @default false
   */
  shouldDisableNativeDragPreview?: boolean
  /**
   * Whether to skip the Safari patch in `setCustomNativeDragPreview`.
   * On iOS, we should skip the patch as it will cause the drag preview to be hidden.
   * @default false
   */
  skipSafariPatch?: boolean
  /**
   * A drag operation has started.
   */
  onDragStart?: (e: ElementEventPayloadMap['onDragStart']) => void
  /**
   * A throttled update of where the the user is currently dragging.
   */
  onDrag?: (e: ElementEventPayloadMap['onDrag']) => void
  /**
   * The user has finished a drag and drop operation.
   * This event may not fire if the element is destroyed in a virtualized list.
   * DO NOT handle reordering logic here.
   */
  onDrop?: (e: ElementEventPayloadMap['onDrop']) => void
}): {
  /**
   * Whether the element is currently being dragged.
   */
  isDragging: typeof isDragging
} => {
  const isDragging = ref(false)

  const itemData = computed(() => makeItemData(data.value, type))
  const itemDataForExternal = computed(() =>
    dataForExternal != null ? makeItemDataForExternal(dataForExternal.value, type) : {},
  )

  const makeElementDraggable = (): CleanupFn => {
    if (elementRef.value == null) return () => {}
    return draggable({
      element: elementRef.value,
      dragHandle: dragHandleElementRef?.value,
      getInitialData: () => itemData.value,
      getInitialDataForExternal: () => itemDataForExternal.value,
      canDrag: ({ input }) => {
        // First check if dragging is disabled via the isDraggable prop
        if (isDraggable?.value === false) return false
        // Check if the drag started from an element with .no-drag class
        // Use elementFromPoint to get the element at the drag start position
        if (input != null) {
          const target = document.elementFromPoint(input.clientX, input.clientY)
          if (target && target instanceof HTMLElement) {
            if (target.closest('.no-drag')) {
              return false
            }
          }
        }

        return true
      },
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        if (shouldDisableNativeDragPreview) {
          disableNativeDragPreview({ nativeSetDragImage })
          return
        }
        const preview = isRef(dragPreview) ? unref(dragPreview) : dragPreview
        if (preview == null) return
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: centerUnderPointer, // Center the drag preview under the pointer.
          render: ({ container }) => {
            return renderNativeDragPreview(container, preview, dragPreviewComponentProps?.value as ComponentProps)
          },
          skipSafariPatch,
        })
      },
      onDragStart: (e) => {
        isDragging.value = true
        onDragStart?.(e)
      },
      onDrag,
      onDrop: (e) => {
        isDragging.value = false
        onDrop?.(e)
      },
    })
  }

  let cleanUp: CleanupFn

  onMounted(() => {
    cleanUp = makeElementDraggable()
  })

  onBeforeUnmount(() => {
    cleanUp?.()
  })

  return {
    isDragging,
  }
}
